import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, errorMessage } from "../api/client";
import type { ListParams } from "../components";

export type Member = { id: string; user_id: string; email: string; first_name: string; last_name: string; full_name: string; status: string; is_owner: boolean; title: string; roles: string[]; role_names: string[]; sites: string[]; last_login: string | null; last_active_at: string | null; created_at: string };
export type Invitation = { id: string; email: string; status: string; roles: string[]; sites: string[]; is_owner: boolean; message: string; invited_by_name: string; expires_at: string; accepted_at: string | null; created_at: string };
export type RoleOption = { id: string; name: string; is_system: boolean };

async function unwrap<T>(p: Promise<{ data?: unknown; error?: unknown }>): Promise<T> {
  const { data, error } = await p;
  if (error) throw error;
  return data as T;
}

export function useMembers(params: ListParams) {
  return useQuery({ queryKey: ["members", params], queryFn: () => unwrap<{ count: number; results: Member[] }>(api.GET("/api/v1/accounts/members/", { params: { query: params as never } })) });
}

export function useInvitations(params: ListParams) {
  return useQuery({ queryKey: ["invitations", params], queryFn: () => unwrap<{ count: number; results: Invitation[] }>(api.GET("/api/v1/accounts/invitations/", { params: { query: params as never } })) });
}

export function useRoleOptions() {
  return useQuery({ queryKey: ["roles", "options"], queryFn: async () => (await unwrap<{ results: RoleOption[] }>(api.GET("/api/v1/accounts/roles/", { params: { query: { page_size: 200 } } }))).results });
}

export function useSiteOptions() {
  return useQuery({ queryKey: ["sites", "options"], queryFn: async () => (await unwrap<{ results: { id: string; code: string; name: string }[] }>(api.GET("/api/v1/accounts/sites/", { params: { query: { page_size: 200 } } }))).results });
}

function useInvalidate(keys: string[][]) {
  const qc = useQueryClient();
  return () => keys.forEach((k) => void qc.invalidateQueries({ queryKey: k }));
}

export function useUpdateMember() {
  const inv = useInvalidate([["members"], ["dashboard"]]);
  return useMutation({ mutationFn: ({ id, ...patch }: Partial<Member> & { id: string }) => unwrap<Member>(api.PATCH("/api/v1/accounts/members/{id}/", { params: { path: { id } }, body: patch as never })), onSuccess: inv });
}

export function useRemoveMember() {
  const inv = useInvalidate([["members"], ["dashboard"]]);
  return useMutation({ mutationFn: async (id: string) => { const { error } = await api.DELETE("/api/v1/accounts/members/{id}/", { params: { path: { id } } }); if (error) throw new Error(errorMessage(error)); }, onSuccess: inv });
}

export function useInvite() {
  const inv = useInvalidate([["invitations"], ["dashboard"]]);
  return useMutation({ mutationFn: (body: { email: string; roles: string[]; sites: string[]; is_owner: boolean; message: string }) => unwrap<Invitation>(api.POST("/api/v1/accounts/invitations/", { body: body as never })), onSuccess: inv });
}

export function useInvitationAction() {
  const inv = useInvalidate([["invitations"]]);
  return useMutation({
    mutationFn: async ({ id, action }: { id: string; action: "resend" | "revoke" }) => {
      const { error } = action === "resend"
        ? await api.POST("/api/v1/accounts/invitations/{id}/resend/", { params: { path: { id } } })
        : await api.DELETE("/api/v1/accounts/invitations/{id}/", { params: { path: { id } } });
      if (error) throw new Error(errorMessage(error));
    },
    onSuccess: inv,
  });
}
